import api from "./api";

export interface Driver {
  address?: string;
  assigned_vehicle_id?: string;
  email?: string;
  full_name?: string;
  id: string;
  license_expiry?: string;
  license_number?: string;
  phone?: string;
  rating?: number;
  status?: string;
  vehicle?: string;
  // Add other driver fields as needed
}

export interface Maintenance {
  date?: string;
  id: string;
  status?: string;
  vehicle?: string;
  // Add other maintenance fields as needed
}

export interface Vehicle {
  assigned_driver: any;
  assignedDriver: any;
  brand: any;
  id: string;
  licensePlate: any;
  make: any;
  model?: string;
  plate?: string;
  plate_number: string | undefined;
  status?: string;
}

class FleetService {
  // Drivers
  async createDriver(data: Partial<Driver>): Promise<Driver> {
    const response = await api.post("/drivers", data);
    return response.data.driver;
  }
  // Vehicle Maintenance
  async createMaintenance(data: Partial<Maintenance>): Promise<Maintenance> {
    const response = await api.post("/vehicle-maintenances", data);
    return response.data.maintenance;
  }
  // Vehicles
  async createVehicle(data: FormData | Partial<Vehicle>): Promise<Vehicle> {
    const config =
      data instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {};
    const response = await api.post("/vehicles", data, config);
    return response.data.vehicle;
  }
  async deleteDriver(id: number | string): Promise<{ success: boolean }> {
    const response = await api.delete(`/drivers/${id}`);
    return response.data;
  }
  async deleteMaintenance(id: number | string): Promise<{ success: boolean }> {
    const response = await api.delete(`/vehicle-maintenances/${id}`);
    return response.data;
  }

  async deleteVehicle(id: number | string): Promise<{ success: boolean }> {
    const response = await api.delete(`/vehicles/${id}`);
    return response.data;
  }
  async getDriver(id: number | string): Promise<Driver> {
    const response = await api.get(`/drivers/${id}`);
    return response.data.driver;
  }
  async getDrivers(userId?: number | string): Promise<Driver[]> {
    const response = await api.get("/drivers");
    const allDrivers =
      response.data.data || response.data.drivers || response.data || [];
    if (userId) {
      return allDrivers.filter(
        (d: any) => String(d.created_by) === String(userId),
      );
    }
    return allDrivers;
  }
  async getMaintenance(id: number | string): Promise<Maintenance> {
    const response = await api.get(`/vehicle-maintenances/${id}`);
    return response.data.maintenance;
  }
  async getMaintenances(userId?: number | string): Promise<Maintenance[]> {
    const response = await api.get("/vehicle-maintenances");
    const allMaintenances =
      response.data.data || response.data.maintenances || response.data || [];
    if (userId) {
      return allMaintenances.filter(
        (m: any) => String(m.created_by) === String(userId),
      );
    }
    return allMaintenances;
  }

  async getVehicle(id: number | string): Promise<Vehicle> {
    const response = await api.get(`/vehicles/${id}`);
    return response.data.vehicle;
  }
  async getVehicles(userId?: number | string): Promise<Vehicle[]> {
    const response = await api.get("/vehicles");
    const allVehicles =
      response.data.data || response.data.vehicles || response.data || [];
    if (userId) {
      return allVehicles.filter(
        (v: any) => String(v.created_by) === String(userId),
      );
    }
    return allVehicles;
  }
  async updateDriver(
    id: number | string,
    data: Partial<Driver>,
  ): Promise<Driver> {
    const response = await api.put(`/drivers/${id}`, data);
    return response.data.driver;
  }
  async updateMaintenance(
    id: number | string,
    data: Partial<Maintenance>,
  ): Promise<Maintenance> {
    const response = await api.put(`/vehicle-maintenances/${id}`, data);
    return response.data.maintenance;
  }
  async updateVehicle(id: number | string, data: any): Promise<Vehicle> {
    const payload = data;
    let config = {};
    let entries: Record<string, any> = {};
    if (data instanceof FormData) {
      for (const [key, value] of data.entries()) {
        entries[key] = value;
      }
      // Debug: print FormData before sending

      console.log("[FleetService.updateVehicle] FormData to be sent:", entries);
      config = { headers: { "Content-Type": "multipart/form-data" } };
    } else {
      entries = payload;

      console.log("[FleetService.updateVehicle] JSON to be sent:", entries);
      config = { headers: { "Content-Type": "application/json" } };
    }
    const endpoint = `/vehicles/${id}`;
    if (!id) {
      console.error(
        "[FleetService.updateVehicle] CRITICAL: id is missing! Endpoint will be incorrect. Data:",
        data,
      );
    }
    try {
      const response = await api.put(endpoint, payload, config);
      return response.data.vehicle;
    } catch (error) {
      console.error("[FleetService.updateVehicle] Error response:", error);
      throw error;
    }
  }
}

export default new FleetService();
